import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import PwaStatus from "@/components/PwaStatus";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Admin from "@/pages/Admin";
import TrialsPublic from "@/pages/TrialsPublic";
import PublicShows from "@/pages/PublicShows";
import { ViewAsScope } from "@/components/viewas/ViewAs";
import PaymentReturn from "@/pages/PaymentReturn";
import TeacherArea from "@/pages/TeacherArea";
import { homeFor } from "@/lib/roles";
import "@/App.css";

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="h-10 w-10 rounded-full border-2 border-primary border-t-transparent animate-spin" />
  </div>
);

function AuthCallback() {
  const { processGoogle } = useAuth();
  const navigate = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const params = new URLSearchParams(window.location.hash.substring(1));
    const sid = params.get("session_id");
    (async () => {
      try {
        const u = await processGoogle(sid);
        window.history.replaceState(null, "", window.location.pathname);
        navigate(homeFor(u.role), { replace: true });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, [processGoogle, navigate]);
  return <Loader />;
}

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to={homeFor(user.role)} replace />;
  return children;
}

function Router() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/prove" element={<TrialsPublic />} />
      <Route path="/spettacoli" element={<PublicShows />} />
      <Route path="/dashboard" element={<Protected role="user"><Dashboard /></Protected>} />
      <Route path="/admin" element={<Protected role="admin"><Admin /></Protected>} />
      <Route path="/admin/vedi-come/:uid" element={<Protected role="admin"><ViewAsScope><Dashboard /></ViewAsScope></Protected>} />
      <Route path="/docente" element={<Protected role="teacher"><TeacherArea /></Protected>} />
      <Route path="/pagamento" element={<Protected><PaymentReturn /></Protected>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Router />
          <PwaStatus />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
